import { type UseQueryOptions, queryOptions } from "@tanstack/react-query";

import { type MethodName, type Tag, apiClient } from "app/modules/api-client/apiClient";
import type { Args, Data } from "app/modules/api-client/queryTypes";
import { getQueryKey } from "app/modules/api-client/utils";
import type { ResponseError } from "app/services/fetchService";

export type QueryOptions<TTag extends Tag, TMethod extends MethodName<TTag>, TQueryData = Data<TTag, TMethod>> = Omit<
    UseQueryOptions<Data<TTag, TMethod>, ResponseError, TQueryData>,
    "queryKey" | "queryFn"
>;

/**
 * A builder class to create Tanstack Query options.
 * It allows to set options and arguments for the query in the type-safe way.
 * The main design motivation is to separate API tag/method name, request arguments and Tanstack Query query options.
 *
 * @param {Tag} tag - The tag of the API method.
 * @param {MethodName<Tag>} methodName - The name of the API method.
 * @returns {QueryBuilder<Tag, MethodName<Tag>>} A new QueryBuilder instance.
 * @example
 * const queryOptions = queryOptionsBuilder("tag", "methodName")
 *   .setOptions({ enabled: true })
 *   .setArgs(arg1, arg2, arg3) // optional
 *   .build();
 *
 * const query = useQuery(queryOptions);
 */
class QueryBuilder<TTag extends Tag, TMethod extends MethodName<TTag>, TQueryData = Data<TTag, TMethod>> {
    private tag: TTag;
    private methodName: TMethod;
    private options: QueryOptions<TTag, TMethod, TQueryData>;
    private args: Args<TTag, TMethod> | undefined;

    constructor(
        tag: TTag,
        methodName: TMethod,
        options: QueryOptions<TTag, TMethod, TQueryData> = {},
        args?: Args<TTag, TMethod>,
    ) {
        this.tag = tag;
        this.methodName = methodName;
        this.options = { ...options };
        this.args = args;
    }

    /**
     * A method to set Tanstack Query options.
     * It allows to set all options except `queryKey` and `queryFn`, which are set automatically.
     * @param initOptions - The options to set on the query, e.g. `enabled`, `select`, `placeholderData`, etc.
     * @returns A new QueryBuilder instance with the options set.
     */
    setOptions(initOptions: QueryOptions<TTag, TMethod, TQueryData>): QueryBuilder<TTag, TMethod, TQueryData> {
        return new QueryBuilder(this.tag, this.methodName, initOptions, this.args);
    }

    /**
     * A convenience method to set the `select` option.
     * @param select - The `select` option to set on the query.
     * @returns A new QueryBuilder instance with the `select` option set.
     */
    setSelect<TData>(select: (data: Data<TTag, TMethod>) => TData): QueryBuilder<TTag, TMethod, TData> {
        return new QueryBuilder<TTag, TMethod, TData>(
            this.tag,
            this.methodName,
            { ...this.options, select } as QueryOptions<TTag, TMethod, TData>,
            this.args,
        );
    }

    /**
     * A convenience method to set the `enabled` option.
     * @param enabled - The `enabled` option to set on the query.
     * @returns A new QueryBuilder instance with the `enabled` option set.
     */
    setEnabled(enabled: QueryOptions<TTag, TMethod, TQueryData>["enabled"]): QueryBuilder<TTag, TMethod, TQueryData> {
        return new QueryBuilder(this.tag, this.methodName, { ...this.options, enabled }, this.args);
    }

    /**
     * A convenience method to set the `placeholderData` option.
     * @param placeholderData - The `placeholderData` option to set on the query.
     * @returns A new QueryBuilder instance with the `placeholderData` option set.
     */
    setPlaceholderData(
        placeholderData: QueryOptions<TTag, TMethod, TQueryData>["placeholderData"],
    ): QueryBuilder<TTag, TMethod, TQueryData> {
        return new QueryBuilder(this.tag, this.methodName, { ...this.options, placeholderData }, this.args);
    }

    /**
     * A method to set the arguments for the query.
     * @param args - The arguments to set on the query.
     * @returns A new QueryBuilder instance with the arguments set.
     */
    setArgs(...args: Args<TTag, TMethod>): QueryBuilder<TTag, TMethod, TQueryData> {
        return new QueryBuilder(this.tag, this.methodName, this.options, args);
    }

    /**
     * A required method to build the query options once all options are set.
     * @returns The query options.
     */
    build(): UseQueryOptions<Data<TTag, TMethod>, ResponseError, TQueryData> {
        return queryOptions({
            ...this.options,
            queryKey: getQueryKey({ tag: this.tag, methodName: this.methodName, args: this.args }),
            queryFn: async ({ signal }) =>
                apiClient[this.tag][this.methodName](...(this.args || []), { signal }) as Promise<Data<TTag, TMethod>>,
        });
    }
}

/**
 * A factory function to create a new QueryBuilder instance.
 * @param {Tag} tag - The tag of the API method.
 * @param {MethodName<Tag>} methodName - The name of the API method.
 * @returns {QueryBuilder<Tag, MethodName<Tag>>} A new QueryBuilder instance.
 */
export function queryOptionsBuilder<TTag extends Tag, TMethod extends MethodName<TTag>>(
    tag: TTag,
    methodName: TMethod,
) {
    return new QueryBuilder(tag, methodName);
}
